/**
 * Pure helpers for the Battery schedule page: UK time handling, customer-facing
 * wording, the draft-window preview and client-side checks.
 *
 * Nothing here is authoritative. The database functions re-check every rule;
 * these exist so the customer sees a clear message before submitting.
 */
import type {
  ActionState, Behaviour, OverrideLimits, OverrideWindow, ScheduleSlot, SlotAction, TariffSummary,
} from '../api/types'

export const UK_TZ = 'Europe/London'
const HALF_HOUR_MS = 30 * 60 * 1000

// ---------------------------------------------------------------------------
// Wording — every option states its battery outcome in the same words
// ---------------------------------------------------------------------------

export const BEHAVIOUR_TEXT: Record<Behaviour, { label: string; explain: string }> = {
  FREE: {
    label: 'Free electricity',
    explain: '0p per kWh. We will fill the battery from the grid, then run your home from the grid while it is free.',
  },
  CHEAP: {
    label: 'Cheaper electricity',
    explain: 'Tell us the price. We will fill the battery from the grid during this window.',
  },
  CHARGE: {
    label: 'Charge the battery',
    explain: 'Fill the battery from the grid, whatever the tariff says.',
  },
  HOLD: {
    label: 'Leave the battery alone',
    explain: 'Your home runs from the grid. The battery neither charges nor discharges.',
  },
  DISCHARGE: {
    label: 'Run your home from the battery',
    explain: 'Use stored energy, down to the reserve level.',
  },
}

export const PRICE_OPTIONS: Behaviour[] = ['FREE', 'CHEAP']
export const ADVANCED_OPTIONS: Behaviour[] = ['CHARGE', 'HOLD', 'DISCHARGE']

export const BEHAVIOUR_ACTION: Record<Behaviour, SlotAction> = {
  FREE: 'CHARGE', CHEAP: 'CHARGE', CHARGE: 'CHARGE', HOLD: 'HOLD', DISCHARGE: 'DISCHARGE',
}

export const ACTION_TEXT: Record<SlotAction, string> = {
  CHARGE: 'charge from the grid',
  HOLD: 'hold (home runs from the grid)',
  DISCHARGE: 'power your home',
}

export const STATUS_TEXT: Record<string, string> = {
  PENDING: 'Pending',
  SCHEDULED: 'Scheduled',
  ACTIVE: 'Active',
  CANCELLING: 'Cancelling',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
}

export const REJECT_TEXT: Record<string, string> = {
  NOT_ENABLED: 'Schedule changes are not switched on for this system.',
  NOT_ELIGIBLE: 'This system could not accept the change.',
  SYSTEM_IN_STANDBY: 'Your system was in standby, so the window could not run.',
  MODE_NOT_ALLOWED: 'That option is not available on this system.',
  OVERLAP: 'It overlapped another window.',
  LIMIT_REACHED: 'You already had the maximum number of upcoming windows.',
  OUT_OF_RANGE: 'It was too long, too far ahead, or started in the past.',
  QUOTA: 'Too many changes were made that day.',
  EXPIRED: 'It had already ended before it could be applied.',
  INVALID: 'Some of the details were not valid.',
}

export function cancelledText(w: OverrideWindow): string {
  if (w.cancel_reason === 'SYSTEM_IN_STANDBY') return 'Cancelled because your system went into standby.'
  if (w.cancel_source === 'ADMIN') return 'Cancelled by EquiVolt.'
  return 'Cancelled by you.'
}

// ---------------------------------------------------------------------------
// Live status chip
// ---------------------------------------------------------------------------

const MODE_TEXT: Record<string, string> = {
  CHARGE: 'Charging from the grid',
  BYPASS: 'Holding — home runs from the grid',
  DISCHARGE: 'Powering your home from the battery',
}

const REASON_TEXT: Record<string, string> = {
  WINDOW: 'your window',
  BATTERY_FULL: 'battery full, holding',
  RESERVE_FLOOR: 'battery at its reserve level, holding',
  NO_TELEMETRY: 'waiting for a reading from your system',
  TARIFF: 'normal tariff plan',
  AGILE: 'Agile price plan',
  DISPATCH: 'smart-charge slot',
  STANDBY: 'system in standby',
  ADMIN_OVERRIDE: 'set by EquiVolt',
}

export type ChipTone = 'charge' | 'hold' | 'discharge' | 'neutral'

/**
 * The chip text. With an active window it leads with the window's state, so a
 * FREE window on a full battery reads "Active — battery full, holding"
 * rather than looking ignored.
 */
export function chipFor(
  action: ActionState | null | undefined,
  activeWindow: OverrideWindow | undefined,
): { text: string; tone: ChipTone } | null {
  if (!action || !action.mode) {
    return activeWindow ? { text: `${STATUS_TEXT[activeWindow.display_status]} — waiting for your system`, tone: 'neutral' } : null
  }
  const tone: ChipTone = action.mode === 'CHARGE' ? 'charge' : action.mode === 'DISCHARGE' ? 'discharge' : 'hold'
  const reason = action.reason_code ? REASON_TEXT[action.reason_code] : undefined

  if (activeWindow) {
    const lead = activeWindow.display_status === 'CANCELLING' ? 'Cancelling' : 'Active'
    if (action.reason_code === 'WINDOW') {
      return { text: `${lead} — ${MODE_TEXT[action.mode].toLowerCase()}`, tone }
    }
    return { text: `${lead} — ${reason ?? MODE_TEXT[action.mode].toLowerCase()}`, tone }
  }
  // The safety reasons already say what the battery is doing.
  if (reason && (action.reason_code === 'BATTERY_FULL' || action.reason_code === 'RESERVE_FLOOR')) {
    return { text: reason.charAt(0).toUpperCase() + reason.slice(1), tone }
  }
  const base = MODE_TEXT[action.mode] ?? action.mode
  return { text: reason ? `${base} · ${reason}` : base, tone }
}

// ---------------------------------------------------------------------------
// UK time
// ---------------------------------------------------------------------------

const partsFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: UK_TZ, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
})

function ukParts(ms: number) {
  const p: Record<string, number> = {}
  for (const { type, value } of partsFmt.formatToParts(new Date(ms))) {
    if (type !== 'literal') p[type] = Number(value)
  }
  return p as { year: number; month: number; day: number; hour: number; minute: number; second: number }
}

/** Offset of UK time from UTC at an instant, in ms (0 in winter, +1h in summer). */
function ukOffsetMs(ms: number): number {
  const p = ukParts(ms)
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000
}

/**
 * UK wall-clock date ("2026-09-22") and time ("09:30") to a UTC instant.
 * Returns null for a time that does not exist (the hour lost when the clocks go forward).
 */
export function ukToUtc(dateStr: string, hhmm: string): Date | null {
  const [y, m, d] = dateStr.split('-').map(Number)
  const [hh, mi] = hhmm.split(':').map(Number)
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mi)) return null
  const wall = Date.UTC(y, m - 1, d, hh, mi)
  let utc = wall - ukOffsetMs(wall)
  utc = wall - ukOffsetMs(utc)
  const back = ukParts(utc)
  if (back.hour !== hh || back.minute !== mi) return null
  return new Date(utc)
}

export function ukDateString(ms: number): string {
  const p = ukParts(ms)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

export const HALF_HOURS: string[] = Array.from({ length: 48 }, (_, i) =>
  `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`)

const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, weekday: 'short', day: 'numeric', month: 'short' })

export function fmtTime(d: Date | string): string {
  return timeFmt.format(typeof d === 'string' ? new Date(d) : d)
}

export function fmtDay(d: Date | string): string {
  return dayFmt.format(typeof d === 'string' ? new Date(d) : d)
}

/** "Tue 22 Sep, 09:00 – 11:00" or "Tue 22 Sep 23:00 – Wed 23 Sep 02:00". */
export function fmtRange(start: string | Date, end: string | Date): string {
  const s = typeof start === 'string' ? new Date(start) : start
  const e = typeof end === 'string' ? new Date(end) : end
  const sameDay = ukDateString(s.getTime()) === ukDateString(e.getTime() - 1)
  return sameDay
    ? `${fmtDay(s)}, ${fmtTime(s)} – ${fmtTime(e)}`
    : `${fmtDay(s)} ${fmtTime(s)} – ${fmtDay(e)} ${fmtTime(e)}`
}

export function floorHalfHour(ms: number): number {
  return Math.floor(ms / HALF_HOUR_MS) * HALF_HOUR_MS
}

// ---------------------------------------------------------------------------
// Tariff band
// ---------------------------------------------------------------------------

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/** 'OFF_PEAK' | 'PEAK' | null (flat or unknown tariff) for the half-hour starting at t. */
export function tariffBand(t: Date, tariff: TariffSummary | null): 'OFF_PEAK' | 'PEAK' | null {
  if (!tariff || tariff.tariff_type !== 'TIME_OF_USE' || !tariff.off_peak_start_local || !tariff.off_peak_end_local) {
    return null
  }
  let minutes: number
  if (tariff.off_peak_time_zone === 'UTC') {
    minutes = t.getUTCHours() * 60 + t.getUTCMinutes()
  } else {
    const p = ukParts(t.getTime())
    minutes = p.hour * 60 + p.minute
  }
  const s = toMinutes(tariff.off_peak_start_local)
  const e = toMinutes(tariff.off_peak_end_local)
  const inWindow = s <= e ? minutes >= s && minutes < e : minutes >= s || minutes < e
  return inWindow ? 'OFF_PEAK' : 'PEAK'
}

// ---------------------------------------------------------------------------
// Preview — overlay a draft window on the pushed baseline, in the browser
// ---------------------------------------------------------------------------

export interface PreviewSlot extends ScheduleSlot {
  before: SlotAction
  changed: boolean
  inDraft: boolean
}

/** Sources that outrank a customer window in the engine, so the draft cannot change them. */
const OUTRANKS_WINDOW = new Set(['ADMIN_OVERRIDE', 'STANDBY'])

export function overlayDraft(
  slots: ScheduleSlot[],
  start: Date | null,
  end: Date | null,
  behaviour: Behaviour | null,
): PreviewSlot[] {
  return slots.map((slot) => {
    const t = new Date(slot.t).getTime()
    const inDraft = !!(start && end && behaviour && t >= start.getTime() && t < end.getTime())
    if (!inDraft || OUTRANKS_WINDOW.has(slot.s)) {
      return { ...slot, before: slot.a, changed: false, inDraft }
    }
    const a = BEHAVIOUR_ACTION[behaviour!]
    return { ...slot, a, s: 'DRAFT', before: slot.a, changed: a !== slot.a, inDraft }
  })
}

/**
 * One or two sentences describing what the draft changes, e.g.
 * "From 06:00 to 09:00 the battery would normally power your home; with this
 *  window it will charge from the grid (and hold once full)."
 */
export function explainDraft(
  preview: PreviewSlot[],
  behaviour: Behaviour | null,
  start: Date | null,
  end: Date | null,
): string | null {
  if (!behaviour || !start || !end) return null
  const inside = preview.filter((p) => p.inDraft)
  if (inside.length === 0) {
    return 'This window is beyond the 48-hour preview. It will be checked when you submit it.'
  }
  const after = BEHAVIOUR_ACTION[behaviour]
  const full = after === 'CHARGE' ? ' (and hold once the battery is full)' : ''
  const blocked = inside.filter((p) => OUTRANKS_WINDOW.has(p.s))

  // Group consecutive slots by their baseline action.
  const runs: { from: Date; to: Date; before: SlotAction }[] = []
  for (const p of inside) {
    if (OUTRANKS_WINDOW.has(p.s)) continue
    const t = new Date(p.t)
    const last = runs[runs.length - 1]
    if (last && last.before === p.before && last.to.getTime() === t.getTime()) {
      last.to = new Date(t.getTime() + HALF_HOUR_MS)
    } else {
      runs.push({ from: t, to: new Date(t.getTime() + HALF_HOUR_MS), before: p.before })
    }
  }

  const sentences: string[] = []
  const same = runs.filter((r) => r.before === after)
  const diff = runs.filter((r) => r.before !== after)
  for (const r of diff.slice(0, 3)) {
    sentences.push(
      `From ${fmtTime(r.from)} to ${fmtTime(r.to)} the battery would normally ${ACTION_TEXT[r.before]}; ` +
      `with this window it will ${ACTION_TEXT[after]}${full}.`,
    )
  }
  if (diff.length > 3) sentences.push('Other times in the window change in the same way.')
  if (same.length && !diff.length) {
    sentences.push(`The battery would already ${ACTION_TEXT[after]} at this time, so nothing changes.`)
    if (behaviour === 'FREE' || behaviour === 'CHEAP') sentences.push('The price is still recorded for your savings statement.')
  } else if (same.length) {
    sentences.push(`Some of this window is already planned to ${ACTION_TEXT[after]}.`)
  }
  if (blocked.length) {
    sentences.push('Part of this window is covered by a setting from EquiVolt or standby, which takes priority.')
  }
  if (end.getTime() > new Date(inside[inside.length - 1].t).getTime() + HALF_HOUR_MS) {
    sentences.push('The preview only covers the next 48 hours.')
  }
  return sentences.join(' ')
}

// ---------------------------------------------------------------------------
// Client-side checks (mirrors of the database rules, for early feedback)
// ---------------------------------------------------------------------------

export function checkDraft(
  start: Date | null,
  end: Date | null,
  behaviour: Behaviour | null,
  price: string,
  limits: OverrideLimits,
  windows: OverrideWindow[],
  now: number,
): string | null {
  if (!start || !end) return 'Choose a start and end time. (Times in the hour the clocks go forward do not exist.)'
  if (!behaviour) return 'Choose what the battery should do.'
  if (end <= start) return 'The end must be after the start.'
  if (start.getTime() < floorHalfHour(now)) return 'The window cannot start in the past.'
  if (end.getTime() - start.getTime() > limits.max_hours * 3600_000) {
    return `A window can be at most ${limits.max_hours} hours long.`
  }
  if (start.getTime() > now + limits.horizon_days * 86_400_000) {
    return `A window can start at most ${limits.horizon_days} days ahead.`
  }
  const open = windows.filter((w) => w.is_open)
  if (open.length >= limits.max_open) {
    return `You already have ${limits.max_open} upcoming windows. Cancel one, or wait for one to finish.`
  }
  const clash = open.find((w) =>
    new Date(w.window_start).getTime() < end.getTime() && new Date(w.window_end).getTime() > start.getTime())
  if (clash) return `This overlaps your window ${fmtRange(clash.window_start, clash.window_end)}.`

  if (behaviour === 'CHEAP' || (price.trim() !== '' && behaviour !== 'FREE')) {
    const v = Number(price)
    if (price.trim() === '' || Number.isNaN(v)) return 'Enter the price in pence per kWh, including VAT.'
    if (v < -100 || v > 200) return 'The price must be between -100p and 200p per kWh.'
    if (!/^-?\d+(\.\d{1,4})?$/.test(price.trim())) return 'Use at most four decimal places for the price.'
  }
  return null
}
